/**
 * SchedulePresetsBox — Sprint 4.7.4
 *
 * Seção "⚡ Presets rápidos" no ScheduleTab. Mostra grid de cards
 * com presets prontos de agenda. Cada card tem:
 *   - Ícone + nome
 *   - Descrição curta
 *   - Resumo da agenda gerada (formatPresetSummary)
 *   - Botão "Aplicar" — preenche o editor local
 *   - Botão "🔬 Pré-visualizar" — abre o SchedulePreviewModal
 *
 * Operador AINDA precisa clicar Aplicar no rodapé do ScheduleTab —
 * presets só preenchem o editor (briefing).
 */
import { useState } from "react";
import { t } from "../../i18n/index.js";
import {
  SCHEDULE_PRESETS,
  clonePresetSchedule,
  formatPresetSummary,
} from "./presets.js";
import SchedulePreviewModal from "./SchedulePreviewModal.jsx";

/**
 * Props:
 *   currentSchedule:  estado atual lido do device (snapshot.schedule)
 *                     usado como "atual" no preview
 *   onApply(schedule): callback quando operador clica "Aplicar preset"
 *                     — recebe a agenda clonada para preencher o editor
 *   collapsed:        bool — começa colapsado (default true para não
 *                     poluir a aba)
 */
export default function SchedulePresetsBox({
  currentSchedule,
  onApply,
  collapsed: collapsedDefault = true,
}) {
  const [collapsed, setCollapsed] = useState(collapsedDefault);
  const [previewPreset, setPreviewPreset] = useState(null);

  const handleApply = (preset) => {
    const cloned = clonePresetSchedule(preset);
    onApply?.(cloned, preset);
  };

  const handlePreview = (preset) => {
    setPreviewPreset(preset);
  };

  const handleClosePreview = () => setPreviewPreset(null);

  const handleApplyFromPreview = () => {
    if (!previewPreset) return;
    handleApply(previewPreset);
    setPreviewPreset(null);
  };

  return (
    <div className="border border-noc-border/60 rounded">
      {/* Header colapsável */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs text-noc-accent hover:bg-noc-accent/10 transition-colors"
      >
        <span className="font-semibold">
          ⚡ {t("schedulePresets.sectionTitle")}
        </span>
        <span className="text-noc-muted/70 text-[10px] flex items-center gap-2">
          {!collapsed && t("schedulePresets.sectionHint")}
          <span>{collapsed ? "▸" : "▾"}</span>
        </span>
      </button>

      {!collapsed && (
        <div className="border-t border-noc-border/60 p-3 space-y-2">
          <div className="text-[10px] text-noc-muted/70">
            {t("schedulePresets.intro")}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SCHEDULE_PRESETS.map((preset) => (
              <PresetCard
                key={preset.id}
                preset={preset}
                onApply={() => handleApply(preset)}
                onPreview={() => handlePreview(preset)}
              />
            ))}
          </div>
          <div className="text-[10px] text-noc-warn italic">
            ⚠ {t("schedulePresets.applyHint")}
          </div>
        </div>
      )}

      {/* Preview reusa o SchedulePreviewModal da Sprint 4.7.2 */}
      <SchedulePreviewModal
        open={!!previewPreset}
        current={currentSchedule}
        proposed={previewPreset ? clonePresetSchedule(previewPreset) : null}
        onClose={handleClosePreview}
        onCopyCurrent={null}
        onRestoreCurrent={null}
        onClearAll={null}
      />

      {/* Botão extra dentro do preview: "Aplicar este preset" */}
      {previewPreset && (
        <PreviewApplyButton
          presetName={t(previewPreset.nameKey)}
          onApply={handleApplyFromPreview}
          onClose={handleClosePreview}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PresetCard — 1 card por preset no grid
// ─────────────────────────────────────────────────────────────────────
function PresetCard({ preset, onApply, onPreview }) {
  return (
    <div className="border border-noc-accent/30 bg-noc-bg/40 rounded p-2 space-y-1.5 hover:border-noc-accent/30 transition-colors">
      <div className="flex items-start gap-2">
        <span className="text-xl leading-none">{preset.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-noc-accent">
            {t(preset.nameKey)}
          </div>
          <div className="text-[10px] text-noc-muted leading-snug">
            {t(preset.descKey)}
          </div>
        </div>
      </div>
      <div className="text-[10px] font-mono text-noc-muted/80 bg-noc-bg/40 border border-noc-border/40 rounded px-1.5 py-1">
        {formatPresetSummary(preset.schedule)}
      </div>
      <div className="flex items-center gap-1 pt-0.5">
        <button
          type="button"
          onClick={onPreview}
          className="flex-1 px-2 py-1 text-[10px] rounded border border-noc-ok/30 text-noc-ok hover:bg-noc-ok/10"
        >
          🔬 {t("schedulePresets.btnPreview")}
        </button>
        <button
          type="button"
          onClick={onApply}
          className="flex-1 px-2 py-1 text-[10px] rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold font-semibold"
        >
          {t("schedulePresets.btnApply")}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PreviewApplyButton — Floating action sobre o modal de preview, no
// canto inferior direito (z-index ACIMA do modal). Permite aplicar o
// preset direto da pré-visualização sem precisar fechar o modal antes.
// ─────────────────────────────────────────────────────────────────────
function PreviewApplyButton({ presetName, onApply, onClose }) {
  return (
    <div
      className="fixed bottom-4 right-4 z-[2250] flex items-center gap-2 bg-noc-panel border border-noc-accent/30 rounded shadow-2xl px-3 py-2"
      style={{ maxWidth: "calc(100% - 2rem)" }}
    >
      <span className="text-[11px] text-noc-muted truncate hidden sm:inline">
        {t("schedulePresets.applyThisLabel", { name: presetName })}
      </span>
      <button
        type="button"
        onClick={onApply}
        className="px-3 py-1 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold font-semibold whitespace-nowrap"
      >
        ✓ {t("schedulePresets.btnApplyPreset")}
      </button>
      <button
        type="button"
        onClick={onClose}
        className="px-2 py-1 text-[11px] rounded border border-noc-border text-noc-muted hover:text-noc-text"
      >
        {t("common.cancel")}
      </button>
    </div>
  );
}
